// AdminAuditLog — Letzte EVE-Interaktionen als Audit-Trail.
// Quelle: GET /admin/audit-log (eve_interactions JOIN users).

import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { selectAccessToken, useAuthStore } from '@/stores/auth-store';

interface AuditRow {
  id: string;
  user_id: string;
  email: string | null;
  ts: number | null;
  user_msg: string | null;
  mode: string | null;
  agent: string | null;
  regime: string | null;
}

function fmtAge(ms: number | null): string {
  if (!ms) return '—';
  const sec = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86_400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86_400)}d ago`;
}

export function AdminAuditLog() {
  const accessToken = useAuthStore(selectAccessToken);
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [limit, setLimit] = useState(50);

  async function reload(currentLimit = limit) {
    if (!accessToken) return;
    setLoading(true);
    try {
      const data = await apiFetch<AuditRow[]>(`/admin/audit-log?limit=${currentLimit}`, {
        accessToken,
      });
      setRows(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  return (
    <div className="glass-strong rounded-2xl p-5">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold">Audit-Log</h2>
          <div className="mt-1 text-[11px] text-neutral-500">
            Letzte {rows.length.toLocaleString('en-US')} EVE-Interaktionen
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={limit}
            onChange={(e) => {
              const n = Number(e.target.value) || 50;
              setLimit(n);
              void reload(n);
            }}
            className="rounded-lg border border-white/10 bg-neutral-900/60 px-2 py-1 text-[12px]"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
          <button
            type="button"
            onClick={() => void reload()}
            aria-label="Aktualisieren"
            className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-2.5 py-1 text-[12px] text-neutral-300 hover:bg-white/5"
          >
            <RefreshCw className={`size-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
          {error}
        </div>
      ) : loading ? (
        <div className="py-8 text-center text-[12px] text-neutral-500">Lade…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 p-6 text-center text-[12px] text-neutral-500">
          Noch keine Interaktionen geloggt.
        </div>
      ) : (
        <ul className="max-h-[60vh] space-y-1.5 overflow-y-auto">
          {rows.map((r) => (
            <li key={r.id} className="rounded-lg border border-white/5 bg-white/[0.02] p-2.5">
              <div className="flex items-center justify-between text-[10px] text-neutral-500">
                <span className="font-mono text-amber-300">{r.email ?? r.user_id.slice(0, 8)}</span>
                <span>{fmtAge(r.ts)}</span>
              </div>
              <div className="mt-0.5 line-clamp-2 text-[12px] text-neutral-200">
                {r.user_msg ?? '—'}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-neutral-500">
                {r.mode ? <span className="rounded bg-neutral-800/60 px-1.5 py-0.5">{r.mode}</span> : null}
                {r.agent ? <span className="rounded bg-neutral-800/60 px-1.5 py-0.5">{r.agent}</span> : null}
                {r.regime ? <span className="italic">regime: {r.regime}</span> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
